import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { ArrowUpRight } from "lucide-react"

import { BalanceStatus } from "@/components/BalanceStatus"
import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatDate } from "@/lib/format"
import { useI18n, t } from "@/lib/i18n"
import { cn } from "@/lib/utils"

import { fetchTrialBalance } from "@/routes/books/api"
import { useBooksFilters } from "@/routes/books/context"
import { BotonDescargar } from "@/components/BotonDescargar"
import { bookToApi } from "@/routes/books/mapping"

const CLASS_LABELS: Record<string, string> = {
  "1": "Activo disponible y exigible",
  "2": "Activo realizable",
  "3": "Activo inmovilizado",
  "4": "Pasivo",
  "5": "Patrimonio",
  "6": "Gastos por naturaleza",
  "7": "Ingresos",
  "8": "Saldos intermediarios / ajustes tributarios",
  "9": "Gastos por funcion",
}

export function TrialBalancePage() {
  const { book, to } = useBooksFilters()
  const { activeTenantId, activeEntityId } = useAuth()
  const { t, term } = useI18n()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["trial-balance", activeTenantId, book, to],
    queryFn: () => fetchTrialBalance({ book, cutoff: to }),
  })

  const rows = data?.rows ?? []

  const grouped = React.useMemo(() => {
    const map = new Map<string, typeof rows>()
    rows.forEach((row) => {
      const key = row.accountCode.charAt(0)
      map.set(key, [...(map.get(key) ?? []), row])
    })
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [rows])

  return (
    <div className="space-y-3">
      {data ? (
        <BalanceStatus
          balanced={data.balanced}
          difference={data.totals.periodDebit - data.totals.periodCredit}
          debit={data.totals.periodDebit}
          credit={data.totals.periodCredit}
          asOf={<span className="code">{formatDate(to)}</span>}
        />
      ) : null}

      <WorkPanel
        actions={
          <BotonDescargar
            recurso="balance-comprobacion"
            filtros={{ entity_id: activeEntityId, libro: bookToApi(book), hasta: to }}
          />
        }
        title={t("balance_comprobacion")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {term.plan_de_cuentas}{" "}{t("· corte")}{" "}<span className="code">{formatDate(to)}</span>
          </span>
        }
        footnote={t("Los subtotales por elemento cierran con doble raya, la convencion contable para decir 'aqui termina y esto es lo que suma'. El codigo de cuenta lleva al mayor de esa cuenta.")}
      >
        <DataState
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={rows.length === 0}
          emptyMessage={t("Sin movimientos hasta la fecha de corte.")}
          emptyHint={t("Adelanta la fecha de corte en la barra superior o revisa si los comprobantes del periodo siguen en la cola de revision.")}
          columns={[8, 30, 10, 10, 10, 10, 10, 10]}
        >
          <Table containerClassName="max-h-[calc(100vh-22rem)]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">{t("cuenta")}</TableHead>
                <TableHead className="min-w-[16rem]">{t("Denominacion")}</TableHead>
                <TableHead className="w-28 text-right">{t("Inicial")}{" "}{t("debe")}</TableHead>
                <TableHead className="w-28 text-right">{t("Inicial")}{" "}{t("haber")}</TableHead>
                <TableHead className="w-28 text-right">{t("Movim.")}{" "}{t("debe")}</TableHead>
                <TableHead className="w-28 text-right">{t("Movim.")}{" "}{t("haber")}</TableHead>
                <TableHead className="w-28 text-right">{t("Final")}{" "}{t("debe")}</TableHead>
                <TableHead className="w-28 text-right">{t("Final")}{" "}{t("haber")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grouped.map(([classKey, classRows]) => {
                const subtotal = classRows.reduce(
                  (acc, row) => ({
                    periodDebit: acc.periodDebit + row.periodDebit,
                    periodCredit: acc.periodCredit + row.periodCredit,
                    closingDebit: acc.closingDebit + row.closingDebit,
                    closingCredit: acc.closingCredit + row.closingCredit,
                  }),
                  { periodDebit: 0, periodCredit: 0, closingDebit: 0, closingCredit: 0 },
                )
                return (
                  <React.Fragment key={classKey}>
                    <TableRow className="row-group hover:bg-muted/60">
                      <TableCell colSpan={8} className="[&]:py-1">
                        <span className="inline-flex items-baseline gap-2">
                          <span className="code grid h-4 w-4 place-items-center rounded-sm bg-primary text-2xs font-semibold text-primary-foreground">
                            {classKey}
                          </span>
                          <span className="text-2xs font-semibold uppercase tracking-[0.07em]">
                            {CLASS_LABELS[classKey] ?? `Elemento ${classKey}`}
                          </span>
                          <span className="text-2xs font-normal normal-case text-muted-foreground">
                            {classRows.length}{" "}{t("cuenta(s)")}</span>
                        </span>
                      </TableCell>
                    </TableRow>

                    {classRows.map((row) => (
                      <TableRow key={row.accountCode}>
                        <TableCell className="code">
                          <Link
                            to={`/contabilidad/libros/mayor?cuenta=${row.accountCode}`}
                            className={cn(
                              "group/link inline-flex items-center gap-1 rounded-sm font-medium",
                              "text-foreground underline-offset-2 hover:text-brand hover:underline",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            )}
                            title={`Analizar la cuenta ${row.accountCode}`}
                          >
                            {row.accountCode}
                            <ArrowUpRight className="h-2.5 w-2.5 opacity-0 transition-opacity group-hover/link:opacity-100" />
                          </Link>
                        </TableCell>
                        <TableCell className="truncate">{row.accountName}</TableCell>
                        <Amount value={row.openingDebit} />
                        <Amount value={row.openingCredit} />
                        <Amount value={row.periodDebit} />
                        <Amount value={row.periodCredit} />
                        <Amount value={row.closingDebit} strong />
                        <Amount value={row.closingCredit} strong />
                      </TableRow>
                    ))}

                    {/* Ruling off del elemento: doble raya, como en el libro. */}
                    <TableRow className="hover:bg-transparent">
                      <TableCell
                        colSpan={4}
                        className="rule-double text-right text-2xs font-semibold uppercase tracking-[0.06em] text-muted-foreground"
                      >
                        {t("Subtotal elemento")}{" "}{classKey}
                      </TableCell>
                      <TableCell className="num rule-double font-semibold">
                        {formatAmount(subtotal.periodDebit)}
                      </TableCell>
                      <TableCell className="num rule-double font-semibold">
                        {formatAmount(subtotal.periodCredit)}
                      </TableCell>
                      <TableCell className="num rule-double font-semibold">
                        {formatAmount(subtotal.closingDebit)}
                      </TableCell>
                      <TableCell className="num rule-double font-semibold">
                        {formatAmount(subtotal.closingCredit)}
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={2} className="text-2xs uppercase tracking-[0.07em]">
                  {t("Totales generales")}</TableCell>
                <TableCell className="num">{formatAmount(data?.totals.openingDebit ?? 0)}</TableCell>
                <TableCell className="num">
                  {formatAmount(data?.totals.openingCredit ?? 0)}
                </TableCell>
                <TableCell className="num">{formatAmount(data?.totals.periodDebit ?? 0)}</TableCell>
                <TableCell className="num">
                  {formatAmount(data?.totals.periodCredit ?? 0)}
                </TableCell>
                <TableCell className="num">{formatAmount(data?.totals.closingDebit ?? 0)}</TableCell>
                <TableCell className="num">
                  {formatAmount(data?.totals.closingCredit ?? 0)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </DataState>
      </WorkPanel>
    </div>
  )
}

/** Celda de importe: el cero se ve apagado, no en blanco. Un hueco no informa. */
function Amount({ value, strong }: { value: number; strong?: boolean }) {
  if (!value) return <TableCell className="num-zero">—</TableCell>
  return <TableCell className={strong ? "num-strong" : "num"}>{formatAmount(value)}</TableCell>
}
